import { useCallback, useEffect, useRef, useState } from 'react';
import {
  contentRect,
  toNormalized,
  toSvgPoints,
  toClient,
  distanceToPoint,
  CLOSE_THRESHOLD_PX,
  MIN_POINTS,
  MAX_POINTS,
  type ContentRect,
  type NormPoint,
} from './roiGeometry';

/**
 * Retail regions on one camera: a queue area, a service counter, or a display to watch.
 *
 * Same drawing interaction and the same overlay as RoiEditor; the polygon is stored
 * normalised (0..1) by the backend (retail_routes). A Queue region may name one of this
 * feed's counting lines as its service point. The Heat toggle paints the 32x18 grids the
 * backend returns: `stored` is the accumulated history, `live` is the running window.
 */

const FALLBACK_FRAME = { width: 640, height: 360 };

export type RegionKind = 'queue' | 'counter' | 'display';
const KINDS: { kind: RegionKind; label: string }[] = [
  { kind: 'queue', label: 'Queue' },
  { kind: 'counter', label: 'Counter' },
  { kind: 'display', label: 'Display' },
];
const KIND_TOKEN: Record<RegionKind, string> = {
  queue: 'var(--color-warning)', counter: 'var(--color-accent)', display: 'var(--color-info)',
};

export type ServedDirection = 'in' | 'out' | null;

export interface Region {
  id: number;
  kind: RegionKind;
  name: string;
  polygon: [number, number][];
  tripwire_id: number | null;
  served_direction?: ServedDirection;
}

interface Heat { w: number; h: number; live: number[][]; stored: number[][]; hours: number; observed_s: number }
interface Line { id: number; name: string }

export interface RetailRegionEditorProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  apiUrl: string;
  authHeaders: (contentType?: boolean) => HeadersInit;
  onClose: () => void;
}

/** Paint one grid, scaled to the picture. Each cell's alpha is its share of the grid max. */
function paintGrid(
  ctx: CanvasRenderingContext2D, grid: number[][], rgb: string, peak: number,
  rect: ContentRect, w: number, h: number,
) {
  if (peak <= 0) return;
  const cw = rect.width / w;
  const ch = rect.height / h;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const v = grid[y]?.[x] ?? 0;
      if (v <= 0) continue;
      ctx.fillStyle = `rgba(${rgb}, ${(0.6 * v) / peak})`;
      ctx.fillRect(rect.left + x * cw, rect.top + y * ch, cw, ch);
    }
  }
}

const gridMax = (g: number[][]) => g.reduce((m, row) => Math.max(m, ...row, 0), 0);

export default function RetailRegionEditor({
  wrapperRef, apiUrl, authHeaders, onClose,
}: RetailRegionEditorProps) {
  const [streamKey, setStreamKey] = useState<string | null>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [rect, setRect] = useState<ContentRect>({ left: 0, top: 0, width: 0, height: 0 });
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Draft being drawn / edited. null = just browsing.
  const [draft, setDraft] = useState<{
    id?: number; kind: RegionKind; name: string; points: NormPoint[]; tripwireId: number | null;
    servedDirection: ServedDirection;
  } | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [cursor, setCursor] = useState<[number, number] | null>(null);
  const [heatOn, setHeatOn] = useState(false);
  const [heat, setHeat] = useState<Heat | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const img = wrapper.querySelector('img.video-stream') as HTMLImageElement | null;
    setRect(contentRect(
      wrapper.clientWidth, wrapper.clientHeight,
      img?.naturalWidth || FALLBACK_FRAME.width, img?.naturalHeight || FALLBACK_FRAME.height,
    ));
  }, [wrapperRef]);

  useEffect(() => {
    measure();
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(measure);
    ro.observe(wrapper);
    const poll = setInterval(measure, 1000);
    return () => { ro.disconnect(); clearInterval(poll); };
  }, [measure, wrapperRef]);

  // --- load: the camera's key, its regions and its counting lines -------- //
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const vs = await fetch(`${apiUrl}/api/drone/video-source`, { headers: authHeaders() });
        if (!vs.ok) throw new Error(`Could not identify the camera (${vs.status})`);
        const key = (await vs.json()).processing_stream_key;
        if (cancelled) return;
        if (!key) { setStatus('Select a video source first'); return; }
        setStreamKey(key);
        const q = encodeURIComponent(key);
        const res = await fetch(`${apiUrl}/api/drone/retail/regions?stream_key=${q}`,
          { headers: authHeaders() });
        if (!res.ok) throw new Error(`Could not load regions (${res.status})`);
        const data = await res.json();
        if (!cancelled) setRegions(data.regions ?? []);
        const gr = await fetch(`${apiUrl}/api/analytics/gates?scope=group&stream_key=${q}`,
          { headers: authHeaders() });
        if (gr.ok && !cancelled) setLines(await gr.json());
      } catch (e) {
        if (!cancelled) setStatus(e instanceof Error ? e.message : 'Could not reach the backend');
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, authHeaders]);

  // --- heat ------------------------------------------------------------- //
  useEffect(() => {
    if (!heatOn || !streamKey) { setHeat(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${apiUrl}/api/drone/retail/heat?stream_key=${encodeURIComponent(streamKey)}&hours=24`,
          { headers: authHeaders() });
        if (!res.ok) { if (!cancelled) setStatus(`Could not load the heat map (${res.status})`); return; }
        const data: Heat = await res.json();
        if (!cancelled) setHeat(data);
      } catch {
        if (!cancelled) setStatus('Could not reach the backend');
      }
    })();
    return () => { cancelled = true; };
  }, [heatOn, streamKey, apiUrl, authHeaders]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!heatOn || !heat) return;
    const rgb = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-accent-rgb').trim() || '63, 182, 139';
    // Stored history first, the live window faintly over it; both normalised to the
    // busiest cell of the pair so they stay comparable.
    const peak = Math.max(gridMax(heat.stored), gridMax(heat.live));
    paintGrid(ctx, heat.stored, rgb, peak, rect, heat.w, heat.h);
    ctx.globalAlpha = 0.4;
    paintGrid(ctx, heat.live, rgb, peak, rect, heat.w, heat.h);
    ctx.globalAlpha = 1;
  }, [heat, heatOn, rect]);

  // --- drawing ---------------------------------------------------------- //
  const handleClick = (e: React.MouseEvent) => {
    if (!drawing || !draft) return;
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    const pts = draft.points;
    if (pts.length >= MIN_POINTS && distanceToPoint(pts[0], x, y, rect) <= CLOSE_THRESHOLD_PX) {
      setDrawing(false);
      setCursor(null);
      return;
    }
    if (pts.length >= MAX_POINTS) { setStatus(`A region can have at most ${MAX_POINTS} points`); return; }
    setDraft({ ...draft, points: [...pts, toNormalized(x, y, rect)] });
    setStatus(null);
  };

  const handleMove = (e: React.PointerEvent) => {
    if (!drawing) return;
    const box = svgRef.current?.getBoundingClientRect();
    if (box) setCursor([e.clientX - box.left, e.clientY - box.top]);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // --- persistence ------------------------------------------------------ //
  const closed = !!draft && !drawing && draft.points.length >= MIN_POINTS;

  const save = async () => {
    if (!draft || !closed) return;
    setBusy(true);
    setStatus(null);
    try {
      const body: Record<string, unknown> = {
        stream_key: streamKey,
        ...(draft.id !== undefined ? { id: draft.id } : {}),
        kind: draft.kind,
        name: draft.name.trim() || KINDS.find(k => k.kind === draft.kind)!.label,
        polygon: draft.points.map(([x, y]) => [x, y]),
      };
      if (draft.kind === 'queue') {
        body.tripwire_id = draft.tripwireId;
        body.served_direction = draft.servedDirection;
      }
      const res = await fetch(`${apiUrl}/api/drone/retail/regions`, {
        method: 'PUT', headers: authHeaders(true), body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        setStatus((err && typeof err.detail === 'string' && err.detail) || `Save failed (${res.status})`);
        return;
      }
      const data = await res.json();
      setRegions(prev => [...prev.filter(r => r.id !== data.region.id), data.region]);
      setDraft(null);
    } catch {
      setStatus('Save failed — backend unreachable');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch(
        `${apiUrl}/api/drone/retail/regions/${id}?stream_key=${encodeURIComponent(streamKey ?? '')}`,
        { method: 'DELETE', headers: authHeaders(true) });
      if (!res.ok && res.status !== 404) { setStatus(`Delete failed (${res.status})`); return; }
      setRegions(prev => prev.filter(r => r.id !== id));
    } catch {
      setStatus('Delete failed — backend unreachable');
    } finally {
      setBusy(false);
    }
  };

  const startNew = () => {
    setDraft({ kind: 'queue', name: '', points: [], tripwireId: null, servedDirection: null });
    setDrawing(true);
    setStatus(null);
  };

  const startEdit = (r: Region) => {
    setDraft({
      id: r.id, kind: r.kind, name: r.name, points: r.polygon as NormPoint[],
      tripwireId: r.tripwire_id, servedDirection: r.served_direction ?? null,
    });
    setDrawing(false);
    setStatus(null);
  };

  // --- render ----------------------------------------------------------- //
  const btn = (extra?: React.CSSProperties): React.CSSProperties => ({
    background: 'var(--color-bg-overlay)', color: 'var(--color-text-primary)',
    border: '1px solid var(--color-border-light)', borderRadius: 3, padding: '3px 9px',
    fontSize: 11, cursor: 'pointer', letterSpacing: '0.04em', ...extra,
  });
  const panel: React.CSSProperties = {
    background: 'var(--color-bg-overlay)', color: 'var(--color-text-primary)', fontSize: 11,
    padding: '3px 8px', borderRadius: 3,
  };
  const remaining = draft ? MIN_POINTS - draft.points.length : 0;

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 5 }}>
      <canvas
        ref={canvasRef}
        width={Math.max(1, Math.round((rect.left * 2) + rect.width))}
        height={Math.max(1, Math.round((rect.top * 2) + rect.height))}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      />
      <svg
        ref={svgRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          cursor: drawing ? 'crosshair' : 'default', touchAction: 'none',
        }}
        onClick={handleClick}
        onPointerMove={handleMove}
        onPointerLeave={() => setCursor(null)}
      >
        {regions.map(r => (
          <polygon
            key={r.id} points={toSvgPoints(r.polygon as NormPoint[], rect)}
            style={{ fill: KIND_TOKEN[r.kind], fillOpacity: 0.12, stroke: KIND_TOKEN[r.kind] }}
            strokeWidth={1.5} pointerEvents="none"
          />
        ))}
        {draft && draft.points.length > 1 && (
          <polyline
            points={toSvgPoints(closed ? [...draft.points, draft.points[0]] : draft.points, rect)}
            style={{ fill: closed ? 'var(--color-info-dim)' : 'none', stroke: 'var(--color-info)' }}
            strokeWidth={2} pointerEvents="none"
          />
        )}
        {drawing && draft && draft.points.length > 0 && cursor && (() => {
          const last = draft.points[draft.points.length - 1];
          const [lx, ly] = toClient(last[0], last[1], rect);
          return (
            <line x1={lx} y1={ly} x2={cursor[0]} y2={cursor[1]} strokeDasharray="4 3"
                  style={{ stroke: 'var(--color-info)' }} strokeWidth={1.5} pointerEvents="none" />
          );
        })()}
        {draft?.points.map((p, i) => {
          const [x, y] = toClient(p[0], p[1], rect);
          return <circle key={i} cx={x} cy={y} r={5} strokeWidth={2}
                         style={{ fill: 'var(--color-bg-overlay)', stroke: 'var(--color-info)' }} />;
        })}
      </svg>

      <div style={{
        position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column',
        gap: 6, maxWidth: 'calc(100% - 16px)', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ ...panel, color: 'var(--color-info)', letterSpacing: '0.05em' }}>
            {draft && drawing
              ? remaining > 0
                ? `REGION — click ${remaining} more point${remaining === 1 ? '' : 's'}`
                : 'REGION — click the first point to close'
              : 'RETAIL REGIONS'}
          </span>
          {!draft && <button style={btn()} onClick={startNew} disabled={!streamKey}>NEW REGION</button>}
          <button
            style={btn(heatOn ? { borderColor: 'var(--color-accent)', color: 'var(--color-accent)' } : undefined)}
            aria-pressed={heatOn} onClick={() => setHeatOn(v => !v)} disabled={!streamKey}>
            HEAT
          </button>
          <button style={btn()} onClick={onClose}>CLOSE</button>
        </div>

        {draft && (
          <div style={{ ...panel, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div role="radiogroup" aria-label="Region kind" style={{ display: 'flex', gap: 8 }}>
              {KINDS.map(k => (
                <label key={k.kind}>
                  <input type="radio" name="region-kind" checked={draft.kind === k.kind}
                         onChange={() => setDraft({ ...draft, kind: k.kind })} /> {k.label}
                </label>
              ))}
            </div>
            <input
              aria-label="Region name" placeholder="Name" maxLength={60} value={draft.name}
              onChange={e => setDraft({ ...draft, name: e.target.value })}
              style={{ fontSize: 11, width: 120 }}
            />
            {draft.kind === 'queue' && (
              <select
                aria-label="Counter line" value={draft.tripwireId ?? ''}
                onChange={e => setDraft({
                  ...draft, tripwireId: e.target.value === '' ? null : Number(e.target.value),
                })}
                style={{ fontSize: 11 }}>
                <option value="">Counter line: none</option>
                {lines.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            {draft.kind === 'queue' && (
              <select
                aria-label="Count served" value={draft.servedDirection ?? ''}
                onChange={e => setDraft({
                  ...draft, servedDirection: e.target.value === '' ? null : e.target.value as 'in' | 'out',
                })}
                style={{ fontSize: 11 }}>
                <option value="">Count served: both directions</option>
                <option value="in">Count served: in</option>
                <option value="out">Count served: out</option>
              </select>
            )}
            {closed && <button style={btn()} onClick={save} disabled={busy}>{busy ? 'SAVING…' : 'SAVE'}</button>}
            {draft.points.length > 0 && (
              <button style={btn()} onClick={() => { setDraft({ ...draft, points: [] }); setDrawing(true); }}>
                REDRAW
              </button>
            )}
            <button style={btn()} onClick={() => { setDraft(null); setDrawing(false); setCursor(null); }}>
              CANCEL
            </button>
          </div>
        )}

        {regions.length > 0 && (
          <ul style={{ ...panel, margin: 0, padding: '4px 8px', listStyle: 'none' }}>
            {regions.map(r => (
              <li key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: KIND_TOKEN[r.kind], textTransform: 'uppercase' }}>{r.kind}</span>
                <span>{r.name}</span>
                {r.kind === 'queue' && r.served_direction && (
                  <span>served: {r.served_direction}</span>
                )}
                <button style={btn({ padding: '0 6px' })} aria-label={`Edit ${r.name}`}
                        onClick={() => startEdit(r)} disabled={busy || !!draft}>
                  EDIT
                </button>
                <button style={btn({ padding: '0 6px' })} aria-label={`Delete ${r.name}`}
                        onClick={() => remove(r.id)} disabled={busy}>
                  DELETE
                </button>
              </li>
            ))}
          </ul>
        )}

        {heatOn && heat && heat.observed_s === 0 && (
          <span style={panel}>No observation in this window</span>
        )}

        {status && (
          <span role="alert" style={{ ...panel, color: 'var(--color-danger)' }}>{status}</span>
        )}
      </div>
    </div>
  );
}
